import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface InlineSummaryRowProps {
  /** Label text (left side) */
  label: string;
  /** Value to display (right side) - can be string, number, or ReactNode */
  value: ReactNode;
  /** Whether to emphasize the value (bold, slightly larger) */
  emphasis?: boolean;
  /** Whether to show a top border (for totals separator) */
  showBorder?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * InlineSummaryRow - Total/count display for sheet summaries.
 * 
 * Label on left, value on right. 
 * Use showBorder=true for totals that need visual separation.
 * 
 * Part of Global Sheet Content System v1.
 * 
 * Usage:
 * <InlineSummaryRow label="Subtotal" value="100₾" />
 * <InlineSummaryRow label="Total" value="130₾" emphasis showBorder />
 */
export function InlineSummaryRow({
  label,
  value,
  emphasis = false,
  showBorder = false,
  className,
}: InlineSummaryRowProps) {
  return (
    <div className={cn(
      "flex items-center justify-between gap-2 py-2 overflow-hidden",
      showBorder && "pt-3 mt-3 border-t border-border/50",
      className
    )}>
      <span className={cn(
        "text-sm min-w-0 truncate",
        emphasis ? "font-medium text-foreground" : "text-muted-foreground"
      )}>
        {label}
      </span>
      <span className={cn(
        "shrink-0 text-right",
        emphasis ? "text-base font-semibold text-foreground" : "text-sm font-medium text-foreground"
      )}>
        {value}
      </span>
    </div>
  );
}
